import React, {ReactElement} from 'react';
import {useSelector} from 'react-redux';
import {DeviceType} from '../../../../../incase-domain/common/DeviceType.enum';
import {IQuestionReportElement} from '../../../../../incase-domain/elements/question/questionReportElement';
import RenderQuestionReport from './RenderQuestionReport';
import {IQuestionElements} from '../../../../../incase-domain/elements/question';
import {useQuestionReportData} from '../../../../../hooks/useQuestionReportData';
import ElementLoadingSkeleton from '../../../../../incase-domain/components/loadingSkeleton/ElementLoadingSkeleton';
import {useLinkedQuestionElement} from './useLinkedQuestionElement';
import {ELEMENT} from '../../../../../incase-domain/elements/base/ElementTypes';
import {QuestionReport, QuestionReportAnswer} from '../../../../../incase-domain/analytics';
import {runFileElementInteractionsSelector} from '../../../../../store/selectors/runfile.selectors';
import {InputElementInteractions} from '../../../../../incase-domain/proptypes/runfile';

interface QuestionReportLiveProps {
    element: IQuestionReportElement;
    device: DeviceType;
}

function localInputValue(
    elementType: string | undefined,
    slideId: string | undefined,
    elementId: string | undefined,
    interactions: Record<string, Record<string, InputElementInteractions>> | undefined
): string | undefined {
    if (elementType !== ELEMENT.TEXT_INPUT || !slideId || !elementId) {
        return undefined;
    }
    return interactions?.[slideId]?.[elementId]?.inputValue;
}

function withLocalInput(report: QuestionReport, localInput?: string): QuestionReport {
    const text = localInput?.trim();
    if (!text) {
        return report;
    }

    const normalized = text.toLowerCase();
    const suppressed = (report.suppressedWords || []).some((word) => word.trim().toLowerCase() === normalized);
    const answers = report.answers || [];
    const alreadyCounted = answers.some((answer) => answer.answerText?.trim().toLowerCase() === normalized);
    if (suppressed || alreadyCounted) {
        return report;
    }

    const localAnswer: QuestionReportAnswer = {answerText: text, noOfAnswers: 1, percentage: 0, type: undefined};
    return {...report, answers: [...answers, localAnswer], totalAnswers: (report.totalAnswers || 0) + 1};
}

// TODO: Question can be on slide that is hidden by rules. So we need to fix that (get state before applying rules)
function QuestionReportLive({element, device}: QuestionReportLiveProps): ReactElement | null {
    const {actualQuestionElement} = useLinkedQuestionElement(element.questionElementId);
    const interactions = useSelector(runFileElementInteractionsSelector);
    const isWordCloud = actualQuestionElement?.type === ELEMENT.TEXT_INPUT;
    const typedValue = localInputValue(
        actualQuestionElement?.type,
        actualQuestionElement?.slideId,
        actualQuestionElement?.id,
        interactions as Record<string, Record<string, InputElementInteractions>> | undefined
    );
    const questionReport = useQuestionReportData(element.questionElementId, typedValue);

    if (!questionReport && !isWordCloud) {
        return <ElementLoadingSkeleton />;
    }

    const report = withLocalInput(
        questionReport || {
            elementId: element.questionElementId || '',
            type: actualQuestionElement?.type || ELEMENT.TEXT_INPUT,
            question: '',
            answers: [],
            slide: {id: '', name: ''},
            totalAnswers: 0,
        },
        typedValue
    );

    return (
        <RenderQuestionReport
            questionReport={report}
            question={actualQuestionElement as IQuestionElements}
            device={device}
            fontFamily={element.fontFamily}
            palette={element.wordCloudPalette}
        />
    );
}

export default QuestionReportLive;
